import React, { useEffect, useState } from "react";
import { driverApi } from "../../services/driverApi";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <p className="text-sm font-semibold text-gray-700">{title}</p>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const INPUT = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const SELECT = `${INPUT} bg-white`;

function toDateValue(d) {
  if (!d) return "";
  try { return new Date(d).toISOString().split("T")[0]; } catch { return ""; }
}

const EMPTY_FORM = {
  dateOfBirth: "",
  address: { street: "", city: "", state: "CO", zip: "" },
  licenseNumber: "",
  licenseState: "CO",
  licenseExpiry: "",
  licenseClass: "",
  dotMedicalNumber: "",
  dotMedicalExpiry: "",
  llcName: "",
  ein: "",
  cbiCheckDate: "",
  cbiExpiryDate: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelationship: "",
};

export default function DriverProfile({ driver: authDriver }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [profileCompleted, setProfileCompleted] = useState(false);

  useEffect(() => {
    driverApi.getProfile()
      .then(({ driver: d }) => {
        setProfileCompleted(d.profileCompleted || false);
        setForm({
          dateOfBirth: toDateValue(d.dateOfBirth),
          address: {
            street: d.address?.street || "",
            city: d.address?.city || "",
            state: d.address?.state || "CO",
            zip: d.address?.zip || "",
          },
          licenseNumber: d.licenseNumber || "",
          licenseState: d.licenseState || "CO",
          licenseExpiry: toDateValue(d.licenseExpiry),
          licenseClass: d.licenseClass || "",
          dotMedicalNumber: d.dotMedicalNumber || "",
          dotMedicalExpiry: toDateValue(d.dotMedicalExpiry),
          llcName: d.llcName || "",
          ein: d.ein || "",
          cbiCheckDate: toDateValue(d.cbiCheckDate),
          cbiExpiryDate: toDateValue(d.cbiExpiryDate),
          emergencyContactName: d.emergencyContactName || "",
          emergencyContactPhone: d.emergencyContactPhone || "",
          emergencyContactRelationship: d.emergencyContactRelationship || "",
        });
      })
      .catch((err) => {
        setLoadError(err.message || "Failed to load profile.");
        setForm(EMPTY_FORM);
      });
  }, []);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function setAddr(field, value) {
    setForm((f) => ({ ...f, address: { ...f.address, [field]: value } }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { driver: updated } = await driverApi.updateProfile(form);
      setProfileCompleted(updated.profileCompleted || false);
      setLoadError("");
      setSuccess("Profile saved successfully.");
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!form) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Profile</h1>
          <p className="text-sm text-gray-500 mt-1">Required by Colorado PUC. Complete all fields to finish enrollment.</p>
        </div>
        {profileCompleted
          ? <span className="text-xs bg-green-100 text-green-700 font-semibold px-2.5 py-1 rounded-full">Complete</span>
          : <span className="text-xs bg-yellow-100 text-yellow-700 font-semibold px-2.5 py-1 rounded-full">Incomplete</span>}
      </div>

      {(loadError || error) && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{loadError || error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 mb-4 text-sm">{success}</div>}

      <form onSubmit={handleSave}>
        {/* Personal Info */}
        <Section title="Personal Information">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name">
              <input value={authDriver?.firstName || ""} disabled className={`${INPUT} bg-gray-50 text-gray-400`} />
            </Field>
            <Field label="Last Name">
              <input value={authDriver?.lastName || ""} disabled className={`${INPUT} bg-gray-50 text-gray-400`} />
            </Field>
          </div>
          <Field label="Date of Birth" required>
            <input type="date" value={form.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} className={INPUT} />
          </Field>
          <Field label="Street Address" required>
            <input value={form.address.street} onChange={(e) => setAddr("street", e.target.value)}
              placeholder="123 Main St" className={INPUT} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <Field label="City" required>
                <input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)}
                  placeholder="Denver" className={INPUT} />
              </Field>
            </div>
            <div>
              <Field label="State">
                <select value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} className={SELECT}>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <div>
              <Field label="ZIP" required>
                <input value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)}
                  placeholder="80202" className={INPUT} />
              </Field>
            </div>
          </div>
        </Section>

        {/* Driver's License */}
        <Section title="Driver's License (Colorado PUC Required)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="License Number" required>
              <input value={form.licenseNumber} onChange={(e) => set("licenseNumber", e.target.value)}
                placeholder="CO license number" className={INPUT} />
            </Field>
            <Field label="License Class">
              <select value={form.licenseClass} onChange={(e) => set("licenseClass", e.target.value)} className={SELECT}>
                <option value="">Select class</option>
                <option value="A">Class A — CDL</option>
                <option value="B">Class B — CDL</option>
                <option value="C">Class C — CDL</option>
                <option value="D">Class D — Standard</option>
                <option value="R">Class R — Restricted</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Issuing State">
              <select value={form.licenseState} onChange={(e) => set("licenseState", e.target.value)} className={SELECT}>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Expiration Date" required>
              <input type="date" value={form.licenseExpiry} onChange={(e) => set("licenseExpiry", e.target.value)} className={INPUT} />
            </Field>
          </div>
        </Section>

        {/* DOT Medical */}
        <Section title="DOT Medical Certificate (Colorado PUC Required)">
          <p className="text-xs text-gray-500 -mt-1 mb-2">Required for vehicles carrying 9 or more passengers. Upload the certificate under "My Documents."</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Medical Examiner Certificate #">
              <input value={form.dotMedicalNumber} onChange={(e) => set("dotMedicalNumber", e.target.value)}
                placeholder="Certificate number" className={INPUT} />
            </Field>
            <Field label="Expiration Date">
              <input type="date" value={form.dotMedicalExpiry} onChange={(e) => set("dotMedicalExpiry", e.target.value)} className={INPUT} />
            </Field>
          </div>
        </Section>

        {/* Business Info */}
        <Section title="Business / Independent Contractor Information">
          <p className="text-xs text-gray-500 -mt-1 mb-2">Required for the Independent Contractor Agreement and WC43 form.</p>
          <Field label="LLC / Business Name">
            <input value={form.llcName} onChange={(e) => set("llcName", e.target.value)}
              placeholder="e.g. Mohamed Gad LLC" className={INPUT} />
          </Field>
          <Field label="EIN — Federal Employer Identification Number">
            <input value={form.ein} onChange={(e) => set("ein", e.target.value)}
              placeholder="XX-XXXXXXX" className={INPUT} />
          </Field>
          <p className="text-xs text-blue-600">
            If you do not yet have an EIN, you may apply free at{" "}
            <span className="font-semibold">irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online</span>
          </p>
        </Section>

        {/* CBI Background Check */}
        <Section title="CBI Background Check (Colorado PUC Required)">
          <p className="text-xs text-gray-500 -mt-1 mb-2">Colorado Bureau of Investigation criminal history check. Enter the dates from your clearance letter.</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Check Date">
              <input type="date" value={form.cbiCheckDate} onChange={(e) => set("cbiCheckDate", e.target.value)} className={INPUT} />
            </Field>
            <Field label="Expiration Date">
              <input type="date" value={form.cbiExpiryDate} onChange={(e) => set("cbiExpiryDate", e.target.value)} className={INPUT} />
            </Field>
          </div>
          <p className="text-xs text-blue-600 mt-1">Upload your CBI clearance letter under the <strong>Docs</strong> tab.</p>
        </Section>

        {/* Emergency Contact */}
        <Section title="Emergency Contact">
          <Field label="Full Name">
            <input value={form.emergencyContactName} onChange={(e) => set("emergencyContactName", e.target.value)}
              placeholder="Contact's full name" className={INPUT} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone Number">
              <input value={form.emergencyContactPhone} onChange={(e) => set("emergencyContactPhone", e.target.value)}
                placeholder="(720) 000-0000" className={INPUT} />
            </Field>
            <Field label="Relationship">
              <select value={form.emergencyContactRelationship} onChange={(e) => set("emergencyContactRelationship", e.target.value)} className={SELECT}>
                <option value="">Select…</option>
                <option value="Spouse">Spouse</option>
                <option value="Parent">Parent</option>
                <option value="Sibling">Sibling</option>
                <option value="Child">Child</option>
                <option value="Friend">Friend</option>
                <option value="Other">Other</option>
              </select>
            </Field>
          </div>
        </Section>

        <button type="submit" disabled={saving}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl text-sm transition">
          {saving ? "Saving…" : "Save Profile"}
        </button>
      </form>
    </div>
  );
}
